/**
 * Bildrechnung für den Scanner — reine Funktionen auf Rastern, ohne DOM.
 *
 * Laufen im Web-Worker (render.worker.ts) und, falls der nicht startet, auf dem
 * Haupt-Thread. Nichts hier fasst ein Canvas an: rein rechnen, damit dieselben
 * Funktionen überall gleich arbeiten und sich ohne Browser prüfen lassen.
 *
 *  - findeBeleg:   Papier vom Untergrund trennen und ein echtes Viereck finden
 *                  (nicht nur einen achsparallelen Kasten — ein schräg liegender
 *                  Bon wird so auch gerade gezogen).
 *  - entzerren:    Viereck → Rechteck (Homographie), beim Verkleinern mit
 *                  Mehrfachabtastung, damit Schrift nicht ausfranst.
 *  - aufbereiten:  Filter „Dokument" / „Farbe": Schatten und ungleiches Licht
 *                  herausrechnen (Hintergrund schätzen und teilen), leicht
 *                  nachschärfen, Schrift kräftigen, Papier weiß. Keine harte
 *                  Schwarz-Weiß-Kurve — blasser Thermodruck bleibt lesbar.
 *  - schaerfe:     Maß für Bildschärfe (Laplace-Energie, kontrastbereinigt).
 */

export type Punkt = { x: number; y: number };
export type Ecken = { tl: Punkt; tr: Punkt; br: Punkt; bl: Punkt };
export type Raster = { data: Uint8ClampedArray; width: number; height: number };
export type Filter = "dokument" | "farbe" | "original";

// ───────────────────────────── Helligkeit ─────────────────────────────

export function grau(r: Raster): Uint8ClampedArray {
  const { data, width, height } = r;
  const n = width * height;
  const g = new Uint8ClampedArray(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) {
    g[i] = (data[j] * 77 + data[j + 1] * 150 + data[j + 2] * 29) >> 8;
  }
  return g;
}

function histogramm(a: ArrayLike<number>): Uint32Array {
  const h = new Uint32Array(256);
  for (let i = 0; i < a.length; i++) h[a[i]]++;
  return h;
}

function perzentil(h: Uint32Array, total: number, p: number): number {
  const ziel = total * p;
  let s = 0;
  for (let v = 0; v < 256; v++) {
    s += h[v];
    if (s >= ziel) return v;
  }
  return 255;
}

/** Otsu-Schwelle: trennt zwei Helligkeitsgruppen (Papier ↔ Untergrund). */
function otsu(h: Uint32Array, total: number): number {
  let sum = 0;
  for (let v = 0; v < 256; v++) sum += v * h[v];
  let sumB = 0,
    wB = 0,
    best = 0,
    schwelle = 127;
  for (let t = 0; t < 256; t++) {
    wB += h[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * h[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const zw = wB * wF * (mB - mF) * (mB - mF);
    if (zw > best) {
      best = zw;
      schwelle = t;
    }
  }
  return schwelle;
}

// ───────────────────────────── Beleg finden ─────────────────────────────

/**
 * Sucht das Papier in einem (klein gerechneten) Bild.
 * Papier = hell UND farbarm; die größte zusammenhängende Fläche gewinnt.
 * Aus ihrem Umriss wird ein Viereck bestimmt: die zwei am weitesten
 * auseinanderliegenden Umrisspunkte (eine Diagonale), dann je Seite der
 * Punkt mit dem größten Abstand zu ihr. Das hält auch Perspektive aus.
 *
 * Ecken kommen in den Koordinaten des übergebenen Rasters zurück.
 */
export function findeBeleg(
  r: Raster,
): { ecken: Ecken; flaeche: number } | null {
  const { data, width: w, height: h } = r;
  const n = w * h;
  if (n < 400) return null;
  const g = new Uint8ClampedArray(n);
  const sat = new Uint8ClampedArray(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) {
    const R = data[j],
      G = data[j + 1],
      B = data[j + 2];
    g[i] = (R * 77 + G * 150 + B * 29) >> 8;
    const mx = R > G ? (R > B ? R : B) : G > B ? G : B;
    const mn = R < G ? (R < B ? R : B) : G < B ? G : B;
    sat[i] = mx === 0 ? 0 : (((mx - mn) * 255) / mx) | 0;
  }
  const hist = histogramm(g);
  // Schwelle: Otsu trennt Papier vom Tisch. Gedeckelt durch das 70. Perzentil,
  // damit ein Beleg, der den Großteil des Bildes füllt, nicht an seinen eigenen
  // Schatten zerfällt.
  const t = Math.max(60, Math.min(otsu(hist, n), perzentil(hist, n, 0.7) - 10));

  const maske = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (g[i] >= t && sat[i] <= 110) maske[i] = 1;

  // Größte Komponente (4er-Nachbarschaft, iterativ)
  const lbl = new Int32Array(n);
  const stapel = new Int32Array(n);
  let bestLbl = 0,
    bestFl = 0,
    naechst = 1;
  for (let s = 0; s < n; s++) {
    if (maske[s] !== 1 || lbl[s] !== 0) continue;
    const L = naechst++;
    let sp = 0,
      fl = 0;
    stapel[sp++] = s;
    lbl[s] = L;
    while (sp > 0) {
      const i = stapel[--sp];
      fl++;
      const x = i % w;
      if (x > 0 && maske[i - 1] === 1 && lbl[i - 1] === 0) { lbl[i - 1] = L; stapel[sp++] = i - 1; }
      if (x < w - 1 && maske[i + 1] === 1 && lbl[i + 1] === 0) { lbl[i + 1] = L; stapel[sp++] = i + 1; }
      if (i >= w && maske[i - w] === 1 && lbl[i - w] === 0) { lbl[i - w] = L; stapel[sp++] = i - w; }
      if (i < n - w && maske[i + w] === 1 && lbl[i + w] === 0) { lbl[i + w] = L; stapel[sp++] = i + w; }
    }
    if (fl > bestFl) {
      bestFl = fl;
      bestLbl = L;
    }
  }
  if (!bestLbl || bestFl < n * 0.03 || bestFl > n * 0.97) return null;

  // Umrisspunkte → konvexe Hülle
  const pts: Punkt[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (lbl[i] !== bestLbl) continue;
      const rand =
        x === 0 || y === 0 || x === w - 1 || y === h - 1 ||
        lbl[i - 1] !== bestLbl || lbl[i + 1] !== bestLbl ||
        lbl[i - w] !== bestLbl || lbl[i + w] !== bestLbl;
      if (rand) pts.push({ x: x + 0.5, y: y + 0.5 });
    }
  }
  const huelle = konvexeHuelle(pts);
  if (huelle.length < 4) return null;

  const q = viereckAusHuelle(huelle);
  if (!q) return null;
  const qa = viereckFlaeche(q);
  const ha = polyFlaeche(huelle);
  // Das Viereck muss die Fläche gut abdecken — sonst war es kein Blatt
  // (z. B. ein Knäuel aus Papier und hellem Tisch).
  if (qa < ha * 0.8 || !istKonvex(q)) return null;
  return { ecken: q, flaeche: qa / n };
}

export function konvexeHuelle(p: Punkt[]): Punkt[] {
  if (p.length < 3) return p.slice();
  const s = p.slice().sort((a, b) => a.x - b.x || a.y - b.y);
  const kreuz = (o: Punkt, a: Punkt, b: Punkt) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const unten: Punkt[] = [];
  for (const q of s) {
    while (unten.length >= 2 && kreuz(unten[unten.length - 2], unten[unten.length - 1], q) <= 0) unten.pop();
    unten.push(q);
  }
  const oben: Punkt[] = [];
  for (let i = s.length - 1; i >= 0; i--) {
    const q = s[i];
    while (oben.length >= 2 && kreuz(oben[oben.length - 2], oben[oben.length - 1], q) <= 0) oben.pop();
    oben.push(q);
  }
  oben.pop();
  unten.pop();
  return unten.concat(oben);
}

function viereckAusHuelle(hl: Punkt[]): Ecken | null {
  // Diagonale: am weitesten entferntes Punktpaar (Hülle ist klein, O(n²) reicht)
  let a = 0,
    b = 0,
    dmax = -1;
  for (let i = 0; i < hl.length; i++) {
    for (let j = i + 1; j < hl.length; j++) {
      const d = (hl[i].x - hl[j].x) ** 2 + (hl[i].y - hl[j].y) ** 2;
      if (d > dmax) {
        dmax = d;
        a = i;
        b = j;
      }
    }
  }
  const A = hl[a],
    B = hl[b];
  let c = -1,
    dc = 0,
    d = -1,
    dd = 0;
  for (let i = 0; i < hl.length; i++) {
    const s = (B.x - A.x) * (hl[i].y - A.y) - (B.y - A.y) * (hl[i].x - A.x);
    if (s > dc) { dc = s; c = i; }
    if (-s > dd) { dd = -s; d = i; }
  }
  if (c < 0 || d < 0) return null;
  return ordneEcken([A, hl[c], B, hl[d]]);
}

/** Vier Punkte in die Reihenfolge oben-links, oben-rechts, unten-rechts, unten-links. */
export function ordneEcken(p: Punkt[]): Ecken {
  const cx = (p[0].x + p[1].x + p[2].x + p[3].x) / 4;
  const cy = (p[0].y + p[1].y + p[2].y + p[3].y) / 4;
  // Im Bild (y nach unten) heißt aufsteigender Winkel: im Uhrzeigersinn.
  const s = p.slice().sort(
    (u, v) => Math.atan2(u.y - cy, u.x - cx) - Math.atan2(v.y - cy, v.x - cx),
  );
  let start = 0,
    min = Infinity;
  for (let i = 0; i < 4; i++) {
    const k = s[i].x + s[i].y;
    if (k < min) { min = k; start = i; }
  }
  const o = [0, 1, 2, 3].map((i) => s[(start + i) % 4]);
  return { tl: o[0], tr: o[1], br: o[2], bl: o[3] };
}

function polyFlaeche(p: Punkt[]): number {
  let a = 0;
  for (let i = 0; i < p.length; i++) {
    const j = (i + 1) % p.length;
    a += p[i].x * p[j].y - p[j].x * p[i].y;
  }
  return Math.abs(a) / 2;
}

export function viereckFlaeche(q: Ecken): number {
  return polyFlaeche([q.tl, q.tr, q.br, q.bl]);
}

function istKonvex(q: Ecken): boolean {
  const p = [q.tl, q.tr, q.br, q.bl];
  let vz = 0;
  for (let i = 0; i < 4; i++) {
    const a = p[i], b = p[(i + 1) % 4], c = p[(i + 2) % 4];
    const k = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (k === 0) continue;
    const s = Math.sign(k);
    if (vz === 0) vz = s;
    else if (s !== vz) return false;
  }
  return true;
}

/** Ecken mit einem Faktor skalieren (z. B. von der Erkennungsgröße aufs Vollbild). */
export function skaliereEcken(q: Ecken, s: number): Ecken {
  const m = (p: Punkt) => ({ x: p.x * s, y: p.y * s });
  return { tl: m(q.tl), tr: m(q.tr), br: m(q.br), bl: m(q.bl) };
}

/** Ausgabegröße für ein Viereck: mittlere Seitenlängen. */
export function zielGroesse(q: Ecken): { w: number; h: number } {
  const d = (a: Punkt, b: Punkt) => Math.hypot(a.x - b.x, a.y - b.y);
  return {
    w: Math.max(1, (d(q.tl, q.tr) + d(q.bl, q.br)) / 2),
    h: Math.max(1, (d(q.tl, q.bl) + d(q.tr, q.br)) / 2),
  };
}

// ───────────────────────────── Entzerren ─────────────────────────────

/** Homographie, die die vier Punkte `von` auf `nach` abbildet (h33 = 1). */
export function homographie(von: Punkt[], nach: Punkt[]): number[] | null {
  const A: number[][] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = von[i];
    const { x: u, y: v } = nach[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y, u]);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y, v]);
  }
  // Gauß mit Spaltenpivot
  for (let c = 0; c < 8; c++) {
    let p = c;
    for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    if (Math.abs(A[p][c]) < 1e-12) return null;
    [A[c], A[p]] = [A[p], A[c]];
    for (let r = 0; r < 8; r++) {
      if (r === c) continue;
      const f = A[r][c] / A[c][c];
      if (f === 0) continue;
      for (let k = c; k < 9; k++) A[r][k] -= f * A[c][k];
    }
  }
  const h = new Array(9);
  for (let i = 0; i < 8; i++) h[i] = A[i][8] / A[i][i];
  h[8] = 1;
  return h;
}

/**
 * Schneidet das Viereck aus und zieht es auf outW×outH gerade.
 * Abtastung bilinear; wenn eine Ausgabezelle mehr als ~1,5 Quellpixel
 * überdeckt, wird mehrfach abgetastet (2×2 oder 3×3) und gemittelt.
 */
export function entzerren(src: Raster, q: Ecken, outW: number, outH: number): Raster {
  const W = Math.max(1, Math.round(outW));
  const Hh = Math.max(1, Math.round(outH));
  const hm = homographie(
    [{ x: 0, y: 0 }, { x: W, y: 0 }, { x: W, y: Hh }, { x: 0, y: Hh }],
    [q.tl, q.tr, q.br, q.bl],
  );
  const out = new Uint8ClampedArray(W * Hh * 4);
  if (!hm) return { data: out, width: W, height: Hh };
  const z = zielGroesse(q);
  const ratio = Math.sqrt((z.w * z.h) / (W * Hh));
  const ss = ratio > 2.2 ? 3 : ratio > 1.4 ? 2 : 1;
  const inv = 1 / ss;
  const sd = src.data,
    sw = src.width,
    sh = src.height;
  const [h0, h1, h2, h3, h4, h5, h6, h7] = hm;
  const n = ss * ss;
  let o = 0;
  for (let y = 0; y < Hh; y++) {
    for (let x = 0; x < W; x++) {
      let r = 0, g = 0, b = 0;
      for (let j = 0; j < ss; j++) {
        const py = y + (j + 0.5) * inv;
        for (let i = 0; i < ss; i++) {
          const px = x + (i + 0.5) * inv;
          const den = h6 * px + h7 * py + 1;
          let fx = (h0 * px + h1 * py + h2) / den - 0.5;
          let fy = (h3 * px + h4 * py + h5) / den - 0.5;
          if (fx < 0) fx = 0; else if (fx > sw - 1) fx = sw - 1;
          if (fy < 0) fy = 0; else if (fy > sh - 1) fy = sh - 1;
          const x0 = fx | 0, y0 = fy | 0;
          const x1 = x0 < sw - 1 ? x0 + 1 : x0;
          const y1 = y0 < sh - 1 ? y0 + 1 : y0;
          const ax = fx - x0, ay = fy - y0;
          const i00 = (y0 * sw + x0) * 4, i10 = (y0 * sw + x1) * 4;
          const i01 = (y1 * sw + x0) * 4, i11 = (y1 * sw + x1) * 4;
          const w00 = (1 - ax) * (1 - ay), w10 = ax * (1 - ay), w01 = (1 - ax) * ay, w11 = ax * ay;
          r += sd[i00] * w00 + sd[i10] * w10 + sd[i01] * w01 + sd[i11] * w11;
          g += sd[i00 + 1] * w00 + sd[i10 + 1] * w10 + sd[i01 + 1] * w01 + sd[i11 + 1] * w11;
          b += sd[i00 + 2] * w00 + sd[i10 + 2] * w10 + sd[i01 + 2] * w01 + sd[i11 + 2] * w11;
        }
      }
      out[o] = r / n;
      out[o + 1] = g / n;
      out[o + 2] = b / n;
      out[o + 3] = 255;
      o += 4;
    }
  }
  return { data: out, width: W, height: Hh };
}

// ───────────────────────────── Aufbereiten ─────────────────────────────

/** Kastenweichzeichner (getrennt, Radius r), auf Float32. */
function kastenBlur(a: Float32Array, w: number, h: number, r: number): Float32Array {
  const t = new Float32Array(w * h);
  const o = new Float32Array(w * h);
  const k = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    const z = y * w;
    let s = 0;
    for (let x = -r; x <= r; x++) s += a[z + Math.min(w - 1, Math.max(0, x))];
    for (let x = 0; x < w; x++) {
      t[z + x] = s / k;
      s += a[z + Math.min(w - 1, x + r + 1)] - a[z + Math.max(0, x - r)];
    }
  }
  for (let x = 0; x < w; x++) {
    let s = 0;
    for (let y = -r; y <= r; y++) s += t[Math.min(h - 1, Math.max(0, y)) * w + x];
    for (let y = 0; y < h; y++) {
      o[y * w + x] = s / k;
      s += t[Math.min(h - 1, y + r + 1) * w + x] - t[Math.max(0, y - r) * w + x];
    }
  }
  return o;
}

/**
 * Papierhelligkeit je Stelle schätzen: das Bild in Kacheln teilen, je Kachel
 * die hellsten Werte nehmen (Schrift ist dunkel, Papier hell), Kacheln ohne
 * Papier aus den Nachbarn füllen, glätten und auf volle Größe hochrechnen.
 */
function hintergrund(L: Uint8ClampedArray, w: number, h: number): Float32Array {
  const B = Math.max(12, Math.round(Math.min(w, h) / 28));
  const gw = Math.ceil(w / B),
    gh = Math.ceil(h / B);
  const kachel = new Float32Array(gw * gh);
  const hk = new Uint32Array(256);
  for (let ky = 0; ky < gh; ky++) {
    for (let kx = 0; kx < gw; kx++) {
      hk.fill(0);
      let cnt = 0;
      const y1 = Math.min(h, (ky + 1) * B), x1 = Math.min(w, (kx + 1) * B);
      for (let y = ky * B; y < y1; y += 2) {
        for (let x = kx * B; x < x1; x += 2) { hk[L[y * w + x]]++; cnt++; }
      }
      kachel[ky * gw + kx] = perzentil(hk, cnt, 0.92);
    }
  }
  // Kacheln, die kaum Papier zeigen (dicke Schrift, Balken, Logo), sind
  // deutlich dunkler als ihre Nachbarn: sie bekommen den Mittelwert der
  // papierhellen Nachbarn. Alle anderen behalten ihren Wert — so folgt die
  // Schätzung auch weichen Schatten, statt sie mit dem hellsten Nachbarn
  // zu überdecken. Zwei Durchgänge, damit auch breite Balken gefüllt werden.
  let cur: Float32Array = kachel;
  for (let pass = 0; pass < 2; pass++) {
    const nx = new Float32Array(cur);
    for (let y = 0; y < gh; y++) {
      for (let x = 0; x < gw; x++) {
        let m = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy;
          if (yy < 0 || yy >= gh) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            if (xx < 0 || xx >= gw) continue;
            const v = cur[yy * gw + xx];
            if (v > m) m = v;
          }
        }
        if (cur[y * gw + x] >= 0.82 * m) continue;
        let s = 0, k = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy;
          if (yy < 0 || yy >= gh) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            if (xx < 0 || xx >= gw) continue;
            const v = cur[yy * gw + xx];
            if (v >= 0.82 * m) { s += v; k++; }
          }
        }
        nx[y * gw + x] = k ? s / k : m;
      }
    }
    cur = nx;
  }
  // Untergrenze: auch unter einem großen dunklen Feld ist Papier nicht
  // schwärzer als die Hälfte des typischen Papiers.
  const hg = histogramm(L);
  const papier = perzentil(hg, L.length, 0.95);
  for (let i = 0; i < cur.length; i++) cur[i] = Math.max(cur[i], papier * 0.5, 24);
  cur = kastenBlur(cur, gw, gh, 1);
  // Bilinear auf volle Größe (Kachelmitten als Stützstellen)
  const bg = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    let fy = (y + 0.5) / B - 0.5;
    if (fy < 0) fy = 0; else if (fy > gh - 1) fy = gh - 1;
    const y0 = fy | 0, y1 = Math.min(gh - 1, y0 + 1), ay = fy - y0;
    for (let x = 0; x < w; x++) {
      let fx = (x + 0.5) / B - 0.5;
      if (fx < 0) fx = 0; else if (fx > gw - 1) fx = gw - 1;
      const x0 = fx | 0, x1 = Math.min(gw - 1, x0 + 1), ax = fx - x0;
      bg[y * w + x] =
        cur[y0 * gw + x0] * (1 - ax) * (1 - ay) + cur[y0 * gw + x1] * ax * (1 - ay) +
        cur[y1 * gw + x0] * (1 - ax) * ay + cur[y1 * gw + x1] * ax * ay;
    }
  }
  return bg;
}

/**
 * Filter „Dokument" (Graustufen) oder „Farbe" — arbeitet in place.
 * 1. Licht ausgleichen: Helligkeit durch geschätzten Papierton teilen.
 * 2. Leicht nachschärfen (Unschärfemaske, Radius 1).
 * 3. Tonkurve: Papier wird weiß, Schrift kräftiger — sanft, keine harte
 *    Schwelle, damit blasser Druck und Stempel erhalten bleiben.
 */
export function aufbereiten(r: Raster, filter: Filter): Raster {
  if (filter === "original") return r;
  const { data, width: w, height: h } = r;
  const n = w * h;
  const L = grau(r);
  const bg = hintergrund(L, w, h);
  const norm = new Float32Array(n);
  for (let i = 0; i < n; i++) norm[i] = Math.min(1.15, L[i] / bg[i]);
  const weich = kastenBlur(norm, w, h, 1);
  const SCHAERFE = 0.8;
  const RAUSCHEN = 0.03;
  // Schwarzpunkt aus dem Inhalt (0,5 %-Perzentil), gedeckelt
  const hq = new Uint32Array(256);
  for (let i = 0; i < n; i += 3) hq[Math.max(0, Math.min(255, (norm[i] * 255) | 0))]++;
  const schwarz = Math.min(0.55, perzentil(hq, Math.ceil(n / 3), 0.005) / 255);
  const weiss = 0.92;
  const span = Math.max(0.2, weiss - schwarz);
  const GAMMA = 2.0;
  for (let i = 0, j = 0; i < n; i++, j += 4) {
    // Nachschärfen mit Schwelle: kleine Unterschiede sind Rauschen (vor allem
    // in aufgehellten Schattenzonen) und werden nicht verstärkt.
    let d = norm[i] - weich[i];
    d = d > RAUSCHEN ? d - RAUSCHEN : d < -RAUSCHEN ? d + RAUSCHEN : 0;
    let v = norm[i] + SCHAERFE * d;
    v = (v - schwarz) / span;
    v = v <= 0 ? 0 : v >= 1 ? 1 : Math.pow(v, GAMMA);
    const y = v * 255;
    if (filter === "dokument") {
      data[j] = data[j + 1] = data[j + 2] = y;
    } else {
      // Farbe: Kanäle im selben Verhältnis wie die Helligkeit anheben
      const f = L[i] > 0 ? y / L[i] : 0;
      data[j] = data[j] * f;
      data[j + 1] = data[j + 1] * f;
      data[j + 2] = data[j + 2] * f;
    }
  }
  return r;
}

// ───────────────────────────── Schärfe ─────────────────────────────

/**
 * Schärfemaß eines Graubildes: mittlere Laplace-Energie der kräftigsten
 * Kanten (oberste 2 %), geteilt durch den Kontrast. Verwackeln und Fokus-
 * Fehler senken es deutlich; Inhaltsmenge (viel/wenig Text) kaum, weil nur
 * die stärksten Kanten zählen. Werte typischerweise 0,05 (matschig) … 0,6.
 */
export function schaerfe(g: Uint8ClampedArray, w: number, h: number): number {
  if (w < 8 || h < 8) return 0;
  const hl = new Uint32Array(1024);
  let cnt = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const v = Math.abs(4 * g[i] - g[i - 1] - g[i + 1] - g[i - w] - g[i + w]);
      hl[v > 1023 ? 1023 : v]++;
      cnt++;
    }
  }
  // Mittelwert der obersten 2 %
  let rest = Math.max(1, Math.round(cnt * 0.02));
  const anz = rest;
  let summe = 0;
  for (let v = 1023; v >= 0 && rest > 0; v--) {
    const k = Math.min(rest, hl[v]);
    summe += k * v;
    rest -= k;
  }
  const top = summe / anz;
  const hg = histogramm(g);
  const kontrast = Math.max(16, perzentil(hg, g.length, 0.95) - perzentil(hg, g.length, 0.05));
  return top / (4 * kontrast);
}
